import { useCurrentEtudeData } from "components/EtudeForm/hooks/useCurrentEtudeData";
import { SavePopup } from "components/popups/SavePopup";
import Button from "components/shared/button";
import ListView from "components/shared/list/listView";
import { ListViewType } from "components/shared/list/useListHook";
import Loader from "components/shared/loader";
import { SvgDownload } from "components/svg";
import { usePopup } from "providers/PopupProvider";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { EtudeStep } from "utils/defines";
import { useAuth } from "utils/hooks/useAuth";
import { DocumentsPopup } from "./DocumentsPopup";
import {
  downloadSchemaUrl,
  getAccessoireRows,
  getAppointRows,
  getBallonRows,
  getEchangeurAPlaquesRows,
  getPACRows,
  getPumpRows,
  getSchemaUrl,
  getSolutionID,
  PacItemType,
} from "./helpers";
import { DownloadButtons } from "./SolutionDetailComponents/DownloadButtons";
import { Section } from "./SolutionDetailComponents/Section";
import { SectionsContainer } from "./SolutionDetailComponents/SectionsContainer";
import { SolutionGraphChart } from "./SolutionDetailComponents/SolutionGraphChart";
import { useGetLocationInfoQuery } from "store/api";
import { SolutionDetailListItem } from "./SolutionDetailComponents/SolutionDetailListItemComponents/SolutionDetailListItem";
import { SolutionDetailListItemTable } from "./SolutionDetailComponents/SolutionDetailListItemComponents/SolutionDetailListItemTable";
import { useDispatch } from "react-redux";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import {
  useUpdateDHPDataMutation,
  useUpdateEtudeDataJsonMutation,
} from "store/api";
import { setEtudeData, setEtudeId } from "store/etudeSlice";
import { Text } from "components/shared/text";
import useTranslation from "utils/hooks/useTranslation";
import {
  getLocalStorageAccessToken,
} from "utils/helpers/localStorage";
import { getMemAppToken } from "utils/helpers/project";
import { safeUrl } from "utils/helpers";

enum DetailTabs {
  Schema,
  Graph,
  Dimensions,
  Degagement,
}

export const SolutionDetails: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const { network } = useSalesNetwork();
  const { etudeId } = useParams();
  const { openPopup, closeLastPopup } = usePopup();
  const { isAuth, isInternal } = useAuth();
  const [blobImage, setBlobImage] = useState<string>("");
  const { solution, etudeData, json, hasEcs, isLoading, service, installationType, hasChauffage, hasTcoupure, hasFXBallon } =
    useCurrentEtudeData();
  const { data: locationInfo } = useGetLocationInfoQuery(
    {
      departement: json?.departement ?? json?.solutionRequestBody?.departement ?? "",
      code: json?.city ?? json?.solutionRequestBody?.ville ?? "",
    },
    { skip: !json?.departement && !json?.solutionRequestBody?.departement },
  );
  const [updateEtudeDataJson, { isLoading: isLoadingEtudeJsonSave }] =
    useUpdateEtudeDataJsonMutation();
  const [updateDHPData] = useUpdateDHPDataMutation();

  const isHybridMono = solution?.circuits?.length && solution?.circuits.length > 1 &&
    solution.circuits_hybrides?.length && solution.circuits_hybrides.length <= 1;
  const isHybrid = json?.installationType == 2;
  const showCPoint = !hasTcoupure && !(hasEcs && !hasChauffage);
  const isDoubleService = hasEcs && hasChauffage;
  const pointCLabelKey = isHybridMono
    ? "FO_DETAILS_GRAPH_BIVALENCE_POINT_HYB_MONO"
    : isDoubleService
      ? "FO_DETAILS_GRAPH_BIVALENCE_POINT_DS"
      : "FO_DETAILS_GRAPH_BIVALENCE_POINT";

  // const jsonToSave = useMemo(() => {
  //   return ;
  // }, [json, solution]);

  // const { handleUpdateJson, isLoadingEtudeJsonSave } = useEtudeUpdate(
  //   undefined,
  //   JSON.stringify({
  //     ...json,
  //     accessoires: null,
  //     selectedSolution: solution,
  //     selectedSolutionID: getSolutionID(solution!),
  //     step: EtudeStep.SolutionDetails,
  //   })
  // );
  const handleUpdateJson = useCallback(async () => {
    let pdfDownloadLink = null;
    let dwgDownloadLink = null;

    if (!!blobImage) {
      Object.entries(solution?.code_schema ?? {}).map(([key, codeSchema]) => {
        if (key === 'pdf') {
          pdfDownloadLink = downloadSchemaUrl(
            `${codeSchema}/${key}?fileDownloadName=${`${codeSchema}.${key}`}`
          )
        } else if (key === 'dwg') {
          dwgDownloadLink = downloadSchemaUrl(
            `${codeSchema}/${key}?fileDownloadName=${`${codeSchema}.${key}`}`
          )
        }
      })
    }

    const etudeUpdateData = {
      id: etudeId && !isNaN(+etudeId) ? +etudeId : 0,
      name: etudeData?.siteName,
      code: network?.code,
      jsonData: JSON.stringify({
        ...json,
        accessoires: null,
        selectedSolution: solution,
        selectedSolutionID: getSolutionID(solution!),
        pdfDownloadLink,
        dwgDownloadLink,
        step: EtudeStep.SolutionDetails,
      }),
    };

    await updateEtudeDataJson({ ...etudeUpdateData }).then((result: any) => {
      if (result.error) {
        throw new Error(result.error.data.title);
      }

      let newPath = location.pathname.replace(":etudeId", result?.data?.id);
      if (etudeId) {
        newPath = location.pathname.replace(
          new RegExp(etudeId, "g"),
          result?.data?.id
        );
      }

      dispatch(setEtudeId(result?.data?.id));
      dispatch(
        setEtudeData({
          ...result?.data,
          etudeId: result?.data?.id,
        })
      );
      navigate(newPath, {
        replace: true,
      });
    });
  }, [blobImage, etudeData, etudeId, json, location.pathname, navigate, network?.code, solution, updateEtudeDataJson, dispatch]);

  const [activeTab, setActiveTab] = useState<DetailTabs | null>(null);

  useEffect(() => {
    if (solution?.code_schema?.pdf) {
      setActiveTab(DetailTabs.Schema);
    }
  }, [solution?.code_schema?.pdf]);

  const setTab = (tab: DetailTabs) => setActiveTab(tab);

  const { hp_dimension_url = "", hp_degagement_url = "" } =
    solution?.pac ?? ({} as PAC);

  const safeImageSrc = useMemo(() => {
    let src = "";
    switch (activeTab) {
      case DetailTabs.Dimensions:
        src = hp_dimension_url;
        break;
      case DetailTabs.Degagement:
        src = hp_degagement_url;
        break;
      default:
        src = blobImage ?? "";
        break;
    }

    return safeUrl(src);
  }, [activeTab, solution, blobImage]);

  useEffect(() => {
    const url =
      solution?.code_schema?.pdf && getSchemaUrl(solution?.code_schema?.pdf!);
    if (url && !blobImage) {
      let token = getLocalStorageAccessToken();
      if (!token) {
        token = getMemAppToken();
      }
      if (token) {
        const headers = {
          Authorization: "Bearer " + token,
        };
        const fetchOptions = {
          method: "GET",
        };
        const params = {
          ...fetchOptions,
          headers: headers,
        };
        fetch(url, params)
          .then((res) => {
            return res.ok ? res.blob() : Promise.reject(res.statusText);
          })
          .then((blob) => {
            setBlobImage(URL.createObjectURL(blob));
          })
          .catch((err) => {
            console.error(err);
            setBlobImage("");
          });
      }
      return;
    }
    setBlobImage("");
  }, [solution?.code_schema?.pdf]);

  const openDocumentPopup = useCallback(
    (product: Accessoire) => {
      openPopup(
        <DocumentsPopup onClose={() => closeLastPopup()} product={product} />
      );
    },
    [openPopup, closeLastPopup]
  );
  const { productionPACItems, ecsProductionItems, accessoireItems } =
    useMemo(() => {
      if (!solution) {
        return {};
      }
      const { panier } = solution;

      const panierItems = panier.map((panierItem) => {
        const {
          prix,
          nom: name,
          previewUrl,
          code,
          description,
          tag,
          quantite,
          documents,
        } = panierItem;
        const rows: {
          label: string;
          value?: string | number | boolean;
        }[] = [];
        let overridenTag;
        switch (tag) {
          case "HP":
            rows.push(...getPACRows(panierItem));
            break;
          case "ballonPrimaire":
            rows.push(...getBallonRows(panierItem, PacItemType.BallonPrimaire));
            break;
          case "appoint":
            rows.push(...getAppointRows(panierItem));
            break;
          case "stockprep":
            rows.push(
              ...getBallonRows(panierItem, PacItemType.BallonSerpentine)
            );
            break;
          case "stockecs":
            rows.push(
              ...getBallonRows(panierItem as any, PacItemType.BallonECS)
            );
            break;
          case "exchange":
            rows.push(...getEchangeurAPlaquesRows(panierItem));
            break;
          case "pompe":
            rows.push(...getPumpRows(panierItem));
            break;
          default:
            // if (accessoires.some(accessoire => code == accessoire.code)) {
            overridenTag = "accessoires";
            rows.push(...getAccessoireRows(panierItem));
            //}
            break;
        }

        return {
          code,
          name,
          description,
          prix: prix,
          previewUrl,
          quantite,
          tag: overridenTag ?? tag,
          documents,
          rows: rows,
        };
      });

      const productionPACItems = panierItems.filter((item) =>
        ["HP", "ballonPrimaire", "appoint"].includes(item.tag)
      );

      const ecsProductionItems = panierItems.filter((item) =>
        ["stockprep", "stockecs", "exchange" /*,'pompe */].includes(item.tag)
      );

      let accessoireItems = panierItems.filter(
        (item) =>
          ![
            "HP",
            "ballonPrimaire",
            "appoint",
            "stockprep",
            "stockecs",
            "exchange",
          ].includes(item.tag)
      );

      const chaudiereCode = solution?.chaudiere?.code;
      const selectedBoilers = json?.accessoires?.selectedBoilers;
      const shouldShowChaudiere = solution?.communicant &&
        !!chaudiereCode &&
        (selectedBoilers ? !!selectedBoilers[chaudiereCode] : true);

      if (shouldShowChaudiere) {
        const chaudiere = solution.chaudiere;
        accessoireItems = [
          ...accessoireItems,
          {
            code: chaudiere.code,
            name: chaudiere.nom,
            description: chaudiere.description,
            prix: chaudiere.prix,
            previewUrl: chaudiere.previewUrl,
            quantite: chaudiere.qte,
            tag: chaudiere.tag ?? "",
            documents: [],
            rows: getAccessoireRows(chaudiere as any),
          },
        ];
      }

      return { productionPACItems, ecsProductionItems, accessoireItems };
    }, [solution, json?.accessoires?.selectedBoilers]);

  const sections = useMemo(
    () =>
      [
        { title: "FO_DETAILS_PAC_MODELS", items: productionPACItems },
        { title: "FO_DETAILS_ECS", items: ecsProductionItems, isEcs: true },
        { title: "FO_DETAILS_CONTENTS", items: accessoireItems },
      ].filter((section) => !section.isEcs || hasEcs),
    [productionPACItems, ecsProductionItems, accessoireItems, hasEcs]
  );

  const graphDataRows = useMemo(() => {
    const tReference = hasTcoupure ? json?.Tcoupure : json?.t_base;

    const graphique = solution?.graphique;
    const puissancePacSeries = graphique?.puissance_pac ?? {};
    const deperChauffageSeries = graphique?.deper_chauffage ?? {};

    const getNearestSeriesValue = (
      series: Record<number, number> | undefined,
      targetTemp?: number | null
    ) => {
      if (!series || targetTemp == null) return undefined;

      const keys = Object.keys(series)
        .map(Number)
        .filter((v) => Number.isFinite(v));

      if (!keys.length) return undefined;

      const nearestKey = keys.reduce((best, current) =>
        Math.abs(current - targetTemp) < Math.abs(best - targetTemp)
          ? current
          : best
      );
      return Math.round(series[nearestKey as keyof typeof series]);
    };

    const pointA = getNearestSeriesValue(puissancePacSeries, tReference);
    const pointB = getNearestSeriesValue(deperChauffageSeries, tReference);
    return [
      {
        label: hasTcoupure ? "FO_DETAILS_GRAPH_TCOUPURE" : "FO_DETAILS_GRAPH_TBASE",
        value: tReference,
      },
      {
        marker: "A",
        label: "FO_DETAILS_GRAPH_POWER",
        value: pointA,
        backgroundColor: "#d32f2f",
      },
      ...(hasEcs && !hasChauffage ? [] : [{
        marker: "B",
        label: hasTcoupure ? "FO_DETAILS_GRAPH_DEP_TCOUPURE" : "FO_DETAILS_GRAPH_DEP_TBASE",
        value: pointB,
        backgroundColor: "#f57c00",
      }]),
      ...(showCPoint ? [{
        marker: "C",
        label: pointCLabelKey,
        value: Math.round((solution as any)?.t_bivalence),
        backgroundColor: "#00695c",
      }] : []),

      ...(installationType == 1 ? [{
        label: "FO_DETAILS_APPOINT",
        value: Math.round((solution as any)?.appoint?.puissance_totale),
      }] : []),
    ];

  }, [json?.TcoupureQuestion, json?.Tcoupure, json?.t_base, solution, locationInfo, installationType]);

  const onUpdateDHPData = useCallback(async () => {
    const pac = solution?.panier.find((item: any) => item.tag === "HP");

    let data: DHPData = {
      object: `${json.siteName}/${json.city}/${pac?.quantite}`,
      commercialReference: pac?.code.toString(),
    };
    await updateDHPData({ data });
  }, [json, solution, updateDHPData]);

  return (
    <view data-gap="60" data-scroll="">
      {(isLoading || isLoadingEtudeJsonSave) && <Loader />}
      <SectionsContainer>
        {sections.map((section, i) => (
          <Section
            title={section.title}
            index={i}
            key={`${section.title}_${i}`}
          >
            <ListView
              view={ListViewType.Block}
              data-template="450"
              dataSource={section.items}
              keyField="code"
              scrollerProps={{ "data-space": "1" }}
              listProps={{ "data-gap": "40" }}
              itemProps={{
                className: "custom",
                "data-contain": "",
                "data-border": "",
                "data-radius": "5",
              }}
              itemComponent={({ data }) => (
                <SolutionDetailListItem
                  {...data}
                  headerButton={
                    data.documents?.length > 0 && (
                      <Button
                        data-background="white"
                        data-position="right"
                        accent
                        data-height="40"
                        text="FO_PRODUCT_DOCUMENTS"
                        onClick={() => openDocumentPopup(data)}
                      >
                        <icon data-svg-size="16">
                          <SvgDownload />
                        </icon>
                      </Button>
                    )
                  }
                >
                  <SolutionDetailListItemTable rows={data.rows} />
                </SolutionDetailListItem>
              )}
            />
          </Section>
        ))}
        <group
          data-max-length="1440"
          data-position="center"
          data-gap="10"
          data-direction="column"
        >
          <group data-gap="20" data-align="center" data-space-left="10">
            <Button
              data-background={!blobImage ? "grey-color-mid" : "white"}
              style={!blobImage ? { opacity: 0.5, filter: "grayscale(0.5)" } : undefined}
              className={activeTab === DetailTabs.Schema ? "primary" : "accent"}
              data-height="40"
              text="FO_DETAILS_SCHEME"
              onClick={() => {
                setTab(DetailTabs.Schema);
              }}
            />
            <Button
              data-background="white"
              // disabled={!blobImage}
              className={activeTab === DetailTabs.Graph ? "primary" : "accent"}
              data-height="40"
              text="FO_DETAILS_GRAPH"
              onClick={() => {
                setTab(DetailTabs.Graph);
              }}
            />
            <Button
              disabled={!hp_dimension_url}
              data-background="white"
              className={
                activeTab === DetailTabs.Dimensions ? "primary" : "accent"
              }
              data-height="40"
              text="FO_DETAILS_DIMENSIONS"
              onClick={() => {
                setTab(DetailTabs.Dimensions);
              }}
            />
            <Button
              disabled={!hp_degagement_url}
              data-background="white"
              className={
                activeTab === DetailTabs.Degagement ? "primary" : "accent"
              }
              data-height="40"
              text="FO_DETAILS_DEGAGMENT"
              onClick={() => {
                setTab(DetailTabs.Degagement);
              }}
            />
            <separator data-vertical="" />

            {solution?.pac.hp_technical_guide_url &&
              network?.code === "ATLFR" && (
                <>
                  <Button
                    accent
                    type="link"
                    data-height="40"
                    onClick={() =>
                      window.open(solution?.pac.hp_technical_guide_url)
                    }
                  >
                    <Text>FO_DETAILS_GUIDE</Text>
                    <icon>open_in_new</icon>
                  </Button>
                  <separator data-vertical="" />
                </>
              )}
            {!hasFXBallon(solution) && !!blobImage && <DownloadButtons solution={solution!} disabled={!blobImage} />}
          </group>
        </group>
        <group
          data-max-length="1440"
          data-position="center"
          data-gap="10"
          data-direction="column"
          data-test="4"
        >
          <group>
            <group data-min-height="30">
              {activeTab === DetailTabs.Schema && t("FO_INFO_SCHEMA")}
            </group>
            {solution?.communicant && activeTab === DetailTabs.Schema && <div
              data-space-top="10"
              dangerouslySetInnerHTML={{
                __html: t("FO_INFO_SCHEMA_ATLANTIC_MANDATORY"),
              }}
            />}
          </group>
          <group data-border="" data-space="80">

            {activeTab === DetailTabs.Graph && (
              <group data-gap="20" data-wrap="no" data-align="start">
                <group data-fit="3" data-min-length="800">
                  <SolutionGraphChart
                    solution={solution}
                    json={json}
                    service={service}
                    locationInfo={locationInfo}
                  />
                </group>
                <group data-fit="1" data-min-length="240" data-direction="column" data-gap="10" data-space-top="20">
                  {graphDataRows.map((row, index) => (
                    <group key={`${row.label}_${index}`} data-gap="8" data-align="center" data-wrap="no">
                      {row.marker && (
                        <group
                          data-length="20"
                          data-height="20"
                          data-align="center"
                          data-justify="center"
                          // data-border="solid"
                          // data-border-color={row.backgroundColor}
                          data-background={row.backgroundColor}
                          data-color="white"
                          data-radius="40"
                          // data-text-size="11"
                          style={{
                            // lineHeight: 1,
                            // flexShrink: 0,
                            backgroundColor: row.backgroundColor,
                          }}
                        >
                          {row.marker}
                        </group>
                      )}
                      <Text data-position="left" data-text-size="14" data-color="grey-color-dark" data-wrap="wrap">
                        {`${t(row.label)}: ${row.value ?? "-"}`}
                      </Text>
                    </group>
                  ))}
                  {isHybrid && showCPoint && <Text data-wrap="wrap">{t("FO_DETAILS_GRAPH_BIVALENCE_NOTE_HYB")}</Text>}
                </group>
              </group>
            )}
            {hasFXBallon(solution) && activeTab !== DetailTabs.Graph ? (
              <div dangerouslySetInnerHTML={{ __html: t("FO_INFO_SCHEMA_EXE_SPE") }} />
            ) : !!safeImageSrc && activeTab !== DetailTabs.Graph ? (
              <picture
                data-border=""
                data-width="auto"
                data-space="80"
                data-align="center"
              >
                <img
                  src={decodeURIComponent(encodeURIComponent(safeImageSrc))}
                  alt="no image"
                />
              </picture>
            ) : activeTab !== DetailTabs.Graph && (
              <div
                dangerouslySetInnerHTML={{
                  __html: t("FO_INFO_SCHEMA_NON_AVAILABLE"),
                }}
              />
            )}
          </group>
        </group>
      </SectionsContainer>

      <group
        data-border="overprint"
        data-background="white"
        data-align="center"
        data-gap="10"
        data-space="20"
        data-justify="end"
        data-sticky="bottom"
      >
        {isAuth && (
          <Button
            onClick={async () => {
              isAuth && (await handleUpdateJson());
              !isLoadingEtudeJsonSave &&
                openPopup(
                  <SavePopup
                    closePopup={closeLastPopup}
                    application={etudeData?.application || ""}
                    subApplication={etudeData?.subApplication}
                    systeme={etudeData?.systeme || ""}
                    formData={{
                      ...json,
                      accessoires: null,
                      selectedSolution: solution,
                      selectedSolutionID: getSolutionID(solution!),
                      step: EtudeStep.SolutionDetails,
                    }}
                  ></SavePopup>
                );
            }}
            disabled={!isAuth}
            primary
            large
            text="Sauvegarder"
            icon="save"
          />
        )}
        <Button
          data-background="secondary"
          data-color="white"
          large
          text="FO_SOLUTION_PROPOSITION_BUTTON"
          icon="check"
          onClick={() => {
            !isInternal && onUpdateDHPData();
            navigate(`accessoires`);
          }}
        />
      </group>
    </view>
  );
};
